import {
  BuildingStorefrontIcon,
  DocumentMagnifyingGlassIcon,
  KeyIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";

import type { FeatureMetadata } from "../feature.types";
import {
  buildCrudPermissions,
  createLazyPlaceholderPage,
} from "../shared/featureFactories";

import { ROLES_META } from "./roles/roles.meta";

const PermissionsPage = lazy(() => import("./permissions/pages/Permissions"));
const RolesPage = lazy(() => import("./roles/pages/Roles"));
const GuardianAccessPage = lazy(() => import("./guardianAccess/pages/GuardianAccess"));
const AuditLogsPage = lazy(() => import("./auditLogs/pages/AuditLogsPage"));
const UserProfilePage = lazy(() => import("./users/pages/UserProfile"));
const UsersPage = lazy(() => import("./users/pages/Users"));

const usersFeature: FeatureMetadata = {
  id: "users",
  moduleName: "userAccess",
  navigation: {
    label: "User Management",
    path: "/users",
    icon: UserGroupIcon,
    order: 1,
  },
  route: {
    path: "/users/*",
    element: UsersPage,
  },
  permissions: buildCrudPermissions("UserAccessManagement", "Users"),
  readiness: "live",
};

const userProfileFeature: FeatureMetadata = {
  id: "user-profile",
  moduleName: "userAccess",
  navigation: {
    label: "My Profile",
    path: "/user-profile",
    icon: UserCircleIcon,
    order: 0,
  },
  route: {
    path: "/user-profile",
    element: UserProfilePage,
  },
  // Profile hub is available to any authenticated user; tabs are permission-driven.
  permissions: {
    view: "",
    create: "",
    edit: "",
    delete: "",
  },
  readiness: "live",
};

type PlaceholderConfig = {
  id: string;
  label: string;
  path: string;
  icon: FeatureMetadata["navigation"]["icon"];
  order: number;
  permissions: FeatureMetadata["permissions"];
};

const placeholderConfigs = [
  {
    id: "school-branches",
    label: "School Branches",
    path: "/school-branches",
    icon: BuildingStorefrontIcon,
    order: 5,
    permissions: buildCrudPermissions("UserAccessManagement", "SchoolBranches"),
  },
] as const satisfies ReadonlyArray<PlaceholderConfig>;

const placeholderFeatures: FeatureMetadata[] = placeholderConfigs.map(
  ({ id, label, path, icon, order, permissions }) => ({
    id,
    moduleName: "userAccess",
    navigation: { label, path, icon, order },
    permissions,
    route: {
      path,
      element: createLazyPlaceholderPage(label),
    },
    readiness: "live",
  }),
);

const permissionsFeature: FeatureMetadata = {
  id: "permissions",
  moduleName: "userAccess",
  navigation: {
    label: "Permissions",
    path: "/permissions",
    icon: ShieldCheckIcon,
    order: 3,
  },
  route: {
    path: "/permissions",
    element: PermissionsPage,
  },
  permissions: buildCrudPermissions("UserAccessManagement", "Permissions", {
    create: "UserAccessManagement.Permissions.view",
    edit: "UserAccessManagement.Permissions.view",
    delete: "UserAccessManagement.Permissions.view",
  }),
  readiness: "live",
};

const auditLogsFeature: FeatureMetadata = {
  id: "audit-logs",
  moduleName: "userAccess",
  navigation: {
    label: "Audit Logs",
    path: "/audit-logs",
    icon: DocumentMagnifyingGlassIcon,
    order: 6,
  },
  route: {
    path: "/audit-logs",
    element: AuditLogsPage,
  },
  permissions: buildCrudPermissions("UserAccessManagement", "AuditLogs", {
    create: "UserAccessManagement.AuditLogs.export",
    edit: "UserAccessManagement.AuditLogs.export",
    delete: "UserAccessManagement.AuditLogs.export",
  }),
  readiness: "live",
};

const rolesFeature: FeatureMetadata = {
  id: "roles",
  moduleName: "userAccess",
  navigation: {
    label: ROLES_META.navigationLabel,
    path: "/roles",
    icon: KeyIcon,
    order: 2,
  },
  route: {
    path: "/roles",
    element: RolesPage,
  },
  permissions: buildCrudPermissions("UserAccessManagement", "Roles"),
  readiness: "live",
};

const guardianAccessFeature: FeatureMetadata = {
  id: "guardian-access",
  moduleName: "userAccess",
  navigation: {
    label: "Guardian Access",
    path: "/guardian-access",
    icon: ShieldCheckIcon,
    order: 7,
  },
  route: {
    path: "/guardian-access",
    element: GuardianAccessPage,
  },
  permissions: buildCrudPermissions("UserAccessManagement", "Roles", {
    view: "UserAccessManagement.Roles.assignPermissions",
    create: "UserAccessManagement.Roles.assignPermissions",
    edit: "UserAccessManagement.Roles.assignPermissions",
    delete: "UserAccessManagement.Roles.assignPermissions",
  }),
  readiness: "live",
};

export const USER_ACCESS_FEATURES = [
  ...placeholderFeatures,
  auditLogsFeature,
  usersFeature,
  userProfileFeature,
  rolesFeature,
  permissionsFeature,
  guardianAccessFeature,
] as const satisfies readonly FeatureMetadata[];
